import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CrownIcon, MonitorIcon, UsersIcon, LightbulbIcon } from 'lucide-react';
export function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-white" id="services" data-id="element-404">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-405">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-406">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-407">
            Що ми робимо
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-408">
            Наші послуги
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-409">
            Спеціалізуємося на пошуку ключових спеціалістів та побудові
            ефективних команд для вашого бізнесу
          </p>
        </motion.div>

        {/* Services Grid — 2x2 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-410">
          {/* Card 1: C-level */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="group relative p-8 rounded-2xl bg-white border border-navy-100 hover:border-navy-200 hover:shadow-lg hover:shadow-navy-100/50 transition-all duration-300 cursor-pointer" data-id="element-411">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-navy-100 text-navy-700 mb-5 group-hover:bg-navy-900 group-hover:text-white transition-colors duration-300" data-id="element-412">
              <CrownIcon className="w-7 h-7" data-id="element-413" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-414">
              Executive Search (C‑level)
            </h3>
            <p className="text-navy-500 leading-relaxed" data-id="element-415">
              Пошук та підбір топ‑менеджерів: CEO, COO, CFO, CTO, CMO та інших
              керівників вищої ланки. Глибоке розуміння ринку та конфіденційний
              підхід до кожного проєкту.
            </p>
          </motion.div>

          {/* Card 2: IT */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="group relative p-8 rounded-2xl bg-white border border-navy-100 hover:border-violet-200 hover:shadow-lg hover:shadow-violet-100/50 transition-all duration-300 cursor-pointer" data-id="element-416">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-violet-100 text-violet-600 mb-5 group-hover:bg-violet-600 group-hover:text-white transition-colors duration-300" data-id="element-417">
              <MonitorIcon className="w-7 h-7" data-id="element-418" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-419">
              Підбір IT‑спеціалістів
            </h3>
            <p className="text-navy-500 leading-relaxed" data-id="element-420">
              Рекрутинг розробників, DevOps, QA, Product Manager, дизайнерів та
              технічних лідерів. Знаємо специфіку IT‑ринку та говоримо однією
              мовою з кандидатами.
            </p>
          </motion.div>

          {/* Card 3: Team building */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="group relative p-8 rounded-2xl bg-white border border-navy-100 hover:border-primary-200 hover:shadow-lg hover:shadow-primary-100/50 transition-all duration-300 cursor-pointer" data-id="element-421">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-100 text-primary-600 mb-5 group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300" data-id="element-422">
              <UsersIcon className="w-7 h-7" data-id="element-423" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-424">
              Комплектація команди
            </h3>
            <p className="text-navy-500 leading-relaxed" data-id="element-425">
              Формування цілих відділів та команд під ключ — від визначення
              структури до виходу останнього спеціаліста. Масовий найм у стислі
              терміни з гарантією якості.
            </p>
          </motion.div>

          {/* Card 4: HR consulting */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="group relative p-8 rounded-2xl bg-white border border-navy-100 hover:border-amber-200 hover:shadow-lg hover:shadow-amber-100/50 transition-all duration-300 cursor-pointer" data-id="element-426">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 mb-5 group-hover:bg-amber-600 group-hover:text-white transition-colors duration-300" data-id="element-427">
              <LightbulbIcon className="w-7 h-7" data-id="element-428" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-429">
              HR‑консалтинг
            </h3>
            <p className="text-navy-500 leading-relaxed" data-id="element-430">
              Аудит HR‑процесів, розробка систем мотивації, побудова
              корпоративної культури та стратегії найму. Допомагаємо
              оптимізувати роботу з персоналом.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}